import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Building2,
  Globe2,
  Info,
  RotateCcw,
  Save,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useWatch } from "react-hook-form";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAmountInput } from "@/shared/components/forms/ColocaAmountInput";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import { ColocaCheckboxField } from "@/shared/components/forms/ColocaCheckboxField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaRadioGroupField } from "@/shared/components/forms/ColocaRadioGroupField";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import { ColocaTextareaField } from "@/shared/components/forms/ColocaTextareaField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { compareDecimalStrings, subtractDecimals } from "@/shared/utils/decimal";

import { useFeeLabels } from "../../hooks/useFeeLabels";
import { useFeeMutations, type CreateManyResult } from "../../hooks/useFeeMutations";
import {
  PaymentFeeKind,
  type CorridorCoverage,
  type PaymentFee,
  type ProviderRailRoute,
} from "../../types/paymentFees.types";
import { corridorLabel, formatAmountText, formatFeeFormula, formatRangeLines } from "../../utils/feeFormat";
import {
  breakevenGross,
  clientTotal,
  colocaMargin,
  computeFee,
  corridorScopeOf,
  feeQueryFor,
  feeScopeOf,
  hasValidFromCollision,
  isZeroPrice,
  resolveFee,
  sameScope,
} from "../../utils/feeMath";
import { organizationsKeepingOwnPrice, priceEditTarget } from "../../utils/feeOverrides";
import { CorridorIdentity } from "./CorridorIdentity";
import {
  buildCorridorPriceSchema,
  buildPricePayload,
  PRICE_STEP_FIELDS,
  seedCorridorPriceForm,
  toIsoInstant,
  type CorridorPriceFormValues,
} from "./corridorPriceFormSchema";
import { candidatePrice } from "./feeFormSchema";
import { FeeTiersEditor } from "./FeeTiersEditor";

export interface EditCorridorPriceIntent {
  coverage: CorridorCoverage;
  feeKind: PaymentFeeKind;
  /** Only for a commission: writes that organization's special price. */
  organizationId: string | null;
  organizationName: string | null;
}

export interface EditCorridorPriceSaved {
  role: "GENERAL" | "SPECIAL" | "COST";
  validFrom: string | null;
}

export interface EditCorridorPriceDialogProps {
  intent: EditCorridorPriceIntent;
  open: boolean;
  onClose: () => void;
  onSaved: (saved: EditCorridorPriceSaved) => void;
  routes: ProviderRailRoute[];
  fees: PaymentFee[];
  now: number;
  exampleAmountFor: (currency: string) => string;
  organizationNames: Record<string, string>;
}

const FORM_ID = "edit-corridor-price-form";

export function EditCorridorPriceDialog({
  intent,
  open,
  onClose,
  onSaved,
  routes,
  fees,
  now,
  exampleAmountFor,
  organizationNames,
}: EditCorridorPriceDialogProps) {
  const { t, activeLanguage } = useTranslations("paymentFees");
  const labels = useFeeLabels();
  const { isSubmitting, createMany } = useFeeMutations();
  const [step, setStep] = useState<"form" | "review">("form");
  const [results, setResults] = useState<CreateManyResult[] | null>(null);

  const { coverage, feeKind, organizationId, organizationName } = intent;
  const route = coverage.route;
  const isCost = feeKind === PaymentFeeKind.PROVIDER_COST;
  const isSpecial = !isCost && organizationId != null;
  const role: EditCorridorPriceSaved["role"] = isCost ? "COST" : isSpecial ? "SPECIAL" : "GENERAL";
  const requiresAck = !isSpecial;
  const name = organizationName ?? organizationId ?? "";
  const currency = route.currency;
  const example = exampleAmountFor(currency);
  const corridorText = corridorLabel(route, labels.directionShort);

  const target = useMemo(
    () => priceEditTarget(route, feeKind, isSpecial ? organizationId : null, fees, routes, now),
    [route, feeKind, isSpecial, organizationId, fees, routes, now],
  );

  const schema = useMemo(
    () =>
      buildCorridorPriceSchema({
        money: t("validation_money"),
        percentage: t("validation_percentage"),
        tierThreshold: t("validation_tier_threshold"),
        tierThresholdUnique: t("validation_tier_threshold_unique"),
        tierFixed: t("validation_tier_fixed"),
        tierPercentage: t("validation_tier_percentage"),
        validFromRequired: t("validation_valid_from_required"),
        reasonRequired: t("validation_reason_required"),
        ackRequired: t("validation_ack_required"),
      }),
    [t],
  );
  const form = useZodForm(schema, seedCorridorPriceForm(target.effective, requiresAck));
  const values = useWatch({ control: form.control }) as CorridorPriceFormValues;
  const candidate = candidatePrice(values);

  const alsoOptions = useMemo(
    () => (isSpecial ? routes.filter((other) => other.id !== route.id && other.currency === currency) : []),
    [isSpecial, routes, route.id, currency],
  );
  const alsoRoutes = alsoOptions.filter((other) => values.alsoRouteIds?.includes(other.id));

  const writesBroad = !isSpecial && values.target === "BROAD" && target.broad != null;
  const affectedRoutes = isSpecial
    ? [route, ...alsoRoutes]
    : writesBroad && target.broad
      ? target.broad.routes
      : [route];

  const writes = affectedRoutes.map((affected) => ({
    key: affected.id,
    route: affected,
    scope: isSpecial
      ? corridorScopeOf(affected, feeKind, organizationId)
      : writesBroad && target.broad
        ? target.broad.scope
        : target.exactScope,
  }));
  // A broad rule is one row for many corridors: write it once.
  const uniqueWrites = writesBroad ? writes.slice(0, 1) : writes;

  const validFromIso = values.effectiveNow ? null : toIsoInstant(values.validFromDate, values.validFromTime);
  const hasCollision = uniqueWrites.some((write) =>
    hasValidFromCollision(feeKind, write.scope, validFromIso, fees),
  );

  /* --------------------------- today vs with the change --------------------------- */

  const commissionRule = resolveFee(
    feeQueryFor(route, PaymentFeeKind.COLOCA_COMMISSION, isSpecial ? organizationId : null),
    fees,
    now,
  );
  const costRule = resolveFee(feeQueryFor(route, PaymentFeeKind.PROVIDER_COST), fees, now);
  const total = (fee: Parameters<typeof computeFee>[0] | null) =>
    fee ? (computeFee(fee, example)?.total ?? null) : null;
  const commissionToday = total(commissionRule);
  const costToday = total(costRule);
  const commissionNew = isCost ? commissionToday : total(candidate);
  const costNew = isCost ? total(candidate) : costToday;
  const model = route.economic_model;

  const comparison = [
    {
      id: "commission",
      label: labels.role[isSpecial ? "SPECIAL" : "GENERAL"],
      today: commissionToday,
      next: commissionNew,
      changes: !isCost,
    },
    { id: "cost", label: t("kind_cost"), today: costToday, next: costNew, changes: isCost },
    {
      id: "client",
      label: t("edit_compare_client"),
      today: clientTotal(commissionToday, costToday, model),
      next: clientTotal(commissionNew, costNew, model),
      changes: true,
    },
    {
      id: "margin",
      label: t("edit_compare_margin"),
      today: colocaMargin(commissionToday, costToday, model),
      next: colocaMargin(commissionNew, costNew, model),
      changes: true,
    },
  ];

  const breakeven = breakevenGross(
    isCost ? commissionRule : candidate,
    isCost ? candidate : costRule,
    model,
    route.direction,
  );
  const breakevenText =
    breakeven?.kind === "THRESHOLD"
      ? t("preview_breakeven", { amount: `${formatAmountText(breakeven.gross, activeLanguage)} ${currency}` })
      : breakeven?.kind === "SOME_REFUSED"
        ? t("preview_breakeven_band")
        : breakeven?.kind === "ALL_REFUSED"
          ? t("preview_breakeven_all_refused")
          : null;

  /* ---------------------------------- review ---------------------------------- */

  const reviewRows = affectedRoutes.map((affected) => {
    const amount = exampleAmountFor(affected.currency);
    const before = resolveFee(feeQueryFor(affected, feeKind), fees, now);
    return {
      route: affected,
      amount,
      before: before ? (computeFee(before, amount)?.total ?? null) : null,
      after: computeFee(candidate, amount)?.total ?? null,
    };
  });
  const keptOrganizations = isCost ? [] : organizationsKeepingOwnPrice(affectedRoutes, fees, now);
  const isZero = !isCost && isZeroPrice(candidate);
  const dropsByHalf =
    !isCost &&
    !isZero &&
    reviewRows.some(
      (row) =>
        row.before != null &&
        row.after != null &&
        compareDecimalStrings(row.before, "0") > 0 &&
        compareDecimalStrings(subtractDecimals(row.before, row.after), row.after) > 0,
    );

  /* ---------------------------------- submit ---------------------------------- */

  const failedKeys = new Set(results?.filter((result) => !result.fee).map((result) => result.key) ?? []);
  const isPartial = results != null && failedKeys.size > 0;

  const goToReview = async () => {
    const valid = await form.trigger([...PRICE_STEP_FIELDS]);
    if (valid && !hasCollision) setStep("review");
  };

  const handleSubmit = async (submitted: CorridorPriceFormValues) => {
    if (hasCollision) return;
    const pending = isPartial ? uniqueWrites.filter((write) => failedKeys.has(write.key)) : uniqueWrites;
    const outcome = await createMany(
      pending.map((write) => ({
        key: write.key,
        payload: buildPricePayload(feeKind, write.scope, submitted),
      })),
    );
    const merged = [...(results?.filter((result) => result.fee) ?? []), ...outcome];
    if (merged.every((result) => result.fee)) {
      onSaved({ role, validFrom: validFromIso });
      return;
    }
    setResults(merged);
  };

  /* ---------------------------------- render ---------------------------------- */

  const title = isCost
    ? t("edit_title_cost")
    : isSpecial
      ? t("edit_title_special", { name })
      : t("edit_title_general");
  const applies = isCost
    ? t("edit_applies_cost")
    : isSpecial
      ? t("edit_applies_special", { name })
      : t("edit_applies_general");

  const formulaOf = (fee: PaymentFee | null) =>
    fee ? formatFeeFormula(fee, activeLanguage, labels.formula) : t("edit_compare_none");
  const money = (value: string | null) =>
    value == null ? "—" : `${formatAmountText(value, activeLanguage)} ${currency}`;

  const toolbar = (
    <div className="space-y-2">
      <CorridorIdentity route={route} />
      <div
        className={cn(
          "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm",
          isSpecial ? "bg-primary/10 text-primary" : "bg-muted text-foreground",
        )}
      >
        {isSpecial ? (
          <Building2 className="size-4 shrink-0" aria-hidden="true" />
        ) : (
          <Globe2 className="size-4 shrink-0" aria-hidden="true" />
        )}
        <span className="font-semibold">{applies}</span>
      </div>
    </div>
  );

  const footer = (
    <div className="flex w-full flex-wrap items-center justify-end gap-2">
      {step === "review" ? (
        <ColocaButton
          type="button"
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => setStep("form")}
          disabled={isSubmitting}
        >
          {t("edit_back")}
        </ColocaButton>
      ) : (
        <ColocaButton type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
          {t("dialog_cancel")}
        </ColocaButton>
      )}
      {requiresAck && step === "form" ? (
        <ColocaButton
          type="button"
          variant="primary"
          iconTrailing={ArrowRight}
          onClick={() => void goToReview()}
          disabled={hasCollision}
        >
          {t("edit_next")}
        </ColocaButton>
      ) : (
        <ColocaButton
          type="submit"
          form={FORM_ID}
          variant="primary"
          icon={isPartial ? RotateCcw : Save}
          loading={isSubmitting}
          disabled={hasCollision}
        >
          {isPartial ? t("save_retry") : t("edit_save")}
        </ColocaButton>
      )}
    </div>
  );

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) onClose();
      }}
      title={step === "review" ? t("review_title") : title}
      description={corridorText}
      toolbar={toolbar}
      footer={footer}
      size="xl"
      mobileFullscreen
    >
      <ColocaForm id={FORM_ID} form={form} onSubmit={handleSubmit}>
        {step === "form" ? (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_19rem]">
            <div className="min-w-0 space-y-5">
              {target.broad && !isSpecial && (
                <ColocaRadioGroupField
                  name="target"
                  label={t("edit_target_label")}
                  items={[
                    {
                      value: "BROAD",
                      label: `${t("edit_target_broad", { count: target.broad.routes.length })} — ${t(
                        "edit_target_broad_hint",
                        {
                          corridors: target.broad.routes
                            .map((covered) => corridorLabel(covered, labels.directionShort))
                            .join(", "),
                        },
                      )}`,
                    },
                    {
                      value: "CORRIDOR",
                      label: `${t("edit_target_corridor")} — ${t("edit_target_corridor_hint")}`,
                    },
                  ]}
                />
              )}

              {isSpecial && coverage.ownRuleShadowed && (
                <ColocaAlert variant="warning" icon={AlertTriangle} text={t("edit_shadowed_note")} />
              )}
              {isSpecial &&
                target.effective?.organization_id === organizationId &&
                !sameScope(feeScopeOf(target.effective), target.exactScope) && (
                  <ColocaAlert
                    variant="secondary"
                    icon={Info}
                    text={t("edit_special_broad_note", { name })}
                  />
                )}
              {isSpecial && !coverage.globalCommission && (
                <ColocaAlert variant="warning" icon={AlertTriangle} text={t("edit_special_no_general")} />
              )}

              <FeeTiersEditor currencyLabel={currency} exampleAmount={example}>
                <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
                  <ColocaAmountInput
                    name="percentage"
                    label={
                      values.useTiers
                        ? `${t("price_base_title")} · ${t("price_percentage_label")}`
                        : t("price_percentage_label")
                    }
                    suffix="%"
                    maxFractionDigits={4}
                    placeholder="0"
                    description={t("price_percentage_helper")}
                  />
                  <ColocaAmountInput
                    name="fixed"
                    label={
                      values.useTiers
                        ? `${t("price_base_title")} · ${t("price_fixed_label")}`
                        : t("price_fixed_label")
                    }
                    suffix={currency}
                    maxFractionDigits={8}
                    groupThousands
                    placeholder="0"
                    description={t("price_fixed_helper")}
                  />
                  <ColocaAmountInput
                    name="minFee"
                    label={t("price_min_label")}
                    suffix={currency}
                    maxFractionDigits={8}
                    groupThousands
                    placeholder="0"
                    description={t("price_min_helper")}
                  />
                  <ColocaAmountInput
                    name="maxFee"
                    label={t("price_max_label")}
                    suffix={currency}
                    maxFractionDigits={8}
                    groupThousands
                    placeholder={t("price_max_placeholder")}
                    description={t("price_max_helper")}
                  />
                </div>
              </FeeTiersEditor>

              <div className="space-y-3">
                <label className="flex items-center gap-2.5 text-sm font-medium">
                  <ColocaSwitch
                    checked={values.effectiveNow ?? true}
                    onCheckedChange={(checked) => form.setValue("effectiveNow", checked)}
                    aria-label={t("eff_now_label")}
                  />
                  {t("eff_now_label")}
                </label>
                {!values.effectiveNow && (
                  <div className="space-y-1.5">
                    <div className="flex flex-wrap items-start gap-2">
                      <div className="w-40">
                        <ColocaInputField name="validFromDate" type="date" label={t("eff_date_label")} />
                      </div>
                      <div className="w-32">
                        <ColocaInputField name="validFromTime" type="time" label={t("eff_time_label")} />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {validFromIso
                        ? t("eff_resolved", {
                            datetime: formatDateTime(validFromIso, { locale: activeLanguage }),
                            offset: utcOffsetLabel(),
                          })
                        : t("eff_pick")}
                    </p>
                  </div>
                )}
                {hasCollision && (
                  <ColocaAlert variant="destructive" icon={AlertCircle} text={t("warn_collision")} />
                )}
              </div>

              {isSpecial && alsoOptions.length > 0 && (
                <AlsoCorridorsField
                  options={alsoOptions}
                  selected={values.alsoRouteIds ?? []}
                  onChange={(ids) => form.setValue("alsoRouteIds", ids, { shouldDirty: true })}
                  currency={currency}
                  priceFor={(other) =>
                    formulaOf(
                      resolveFee(
                        feeQueryFor(other, PaymentFeeKind.COLOCA_COMMISSION, organizationId),
                        fees,
                        now,
                      ),
                    )
                  }
                  results={results}
                />
              )}

              {!requiresAck && (
                <ColocaTextareaField
                  name="reason"
                  label={t("reason_label")}
                  description={t("reason_helper")}
                  placeholder={t("reason_placeholder")}
                  rows={2}
                  maxLength={500}
                  count
                />
              )}

              {isPartial && (
                <ColocaAlert
                  variant="destructive"
                  icon={AlertCircle}
                  text={t("save_partial", {
                    ok: (results?.length ?? 0) - failedKeys.size,
                    total: results?.length ?? 0,
                  })}
                />
              )}
            </div>

            <aside
              className="space-y-3 rounded-xl border border-border bg-muted/40 p-4 lg:self-start"
              aria-live="polite"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("edit_compare_title")}
              </p>
              <p className="text-sm">
                {t("edit_compare_amount", {
                  amount: `${formatAmountText(example, activeLanguage)} ${currency}`,
                })}
              </p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] text-muted-foreground">
                    <th className="pb-1 text-left font-medium" />
                    <th className="pb-1 text-right font-medium">{t("edit_compare_today")}</th>
                    <th className="pb-1 text-right font-medium">{t("edit_compare_new")}</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row) => (
                    <tr key={row.id} className={cn(row.id === "client" && "border-t border-border")}>
                      <td className="py-1 pr-2 text-xs text-muted-foreground">{row.label}</td>
                      <td className="py-1 text-right font-mono text-xs tabular-nums">{money(row.today)}</td>
                      <td
                        className={cn(
                          "py-1 text-right font-mono text-xs font-semibold tabular-nums",
                          row.id === "margin" &&
                            row.next != null &&
                            compareDecimalStrings(row.next, "0") < 0 &&
                            "text-destructive",
                          row.changes && row.next !== row.today && "text-primary",
                        )}
                      >
                        {money(row.next)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="space-y-1 border-t border-border pt-2 text-xs">
                <p className="text-muted-foreground">{t("edit_compare_today")}</p>
                <p className="font-mono">{formulaOf(isCost ? costRule : commissionRule)}</p>
                {(isCost ? costRule : commissionRule)?.tiers?.length ? (
                  <RangeList fee={(isCost ? costRule : commissionRule) as PaymentFee} />
                ) : null}
              </div>
              {breakevenText && (
                <ColocaAlert
                  variant={breakeven?.kind === "ALL_REFUSED" ? "destructive" : "warning"}
                  icon={AlertTriangle}
                  text={breakevenText}
                />
              )}
            </aside>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm">
              {t(isCost ? "review_intro_cost" : "review_intro_general", { count: reviewRows.length })}
            </p>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full min-w-[28rem] text-sm">
                <thead className="bg-muted/50 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">{t("review_th_corridor")}</th>
                    <th className="px-3 py-2 text-right font-medium">{t("review_th_before")}</th>
                    <th className="px-3 py-2 text-right font-medium">{t("review_th_after")}</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewRows.map((row) => (
                    <tr key={row.route.id} className="border-t border-border">
                      <td className="px-3 py-2">
                        <CorridorIdentity route={row.route} className="mb-0.5" />
                        <span className="text-[11px] text-muted-foreground">
                          {t("edit_compare_amount", {
                            amount: `${formatAmountText(row.amount, activeLanguage)} ${row.route.currency}`,
                          })}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">
                        {row.before == null
                          ? "—"
                          : `${formatAmountText(row.before, activeLanguage)} ${row.route.currency}`}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums text-primary">
                        {row.after == null
                          ? "—"
                          : `${formatAmountText(row.after, activeLanguage)} ${row.route.currency}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {isZero && (
              <ColocaAlert variant="destructive" icon={AlertCircle} text={t("review_zero_warning")} />
            )}
            {dropsByHalf && (
              <ColocaAlert variant="warning" icon={AlertTriangle} text={t("review_drop_warning")} />
            )}

            {!isCost &&
              (keptOrganizations.length > 0 ? (
                <div className="space-y-2 text-sm">
                  <p>{t("review_orgs_kept", { count: keptOrganizations.length })}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {keptOrganizations.map((id) => (
                      <ColocaBadge key={id} variant="primary" className="font-sans">
                        {organizationNames[id] ?? id}
                      </ColocaBadge>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t("review_orgs_none")}</p>
              ))}

            <ColocaTextareaField
              name="reason"
              label={t("reason_label")}
              description={t("reason_helper")}
              placeholder={t("reason_placeholder")}
              rows={2}
              maxLength={500}
              count
            />
            <ColocaCheckboxField name="acknowledged" label={t(isCost ? "review_ack_cost" : "review_ack")} />

            {isPartial && (
              <ColocaAlert
                variant="destructive"
                icon={AlertCircle}
                text={t("save_partial", {
                  ok: (results?.length ?? 0) - failedKeys.size,
                  total: results?.length ?? 0,
                })}
              />
            )}
          </div>
        )}
      </ColocaForm>
    </ColocaDialog>
  );
}

function RangeList({ fee }: { fee: PaymentFee }) {
  const { activeLanguage } = useTranslations("paymentFees");
  const labels = useFeeLabels();
  return (
    <ul className="space-y-0.5 font-mono text-[11px] text-muted-foreground">
      {formatRangeLines(fee, activeLanguage, labels.formula).map((line) => (
        <li key={line.label}>
          {line.label}: {line.price}
        </li>
      ))}
    </ul>
  );
}

function AlsoCorridorsField({
  options,
  selected,
  onChange,
  currency,
  priceFor,
  results,
}: {
  options: ProviderRailRoute[];
  selected: string[];
  onChange: (ids: string[]) => void;
  currency: string;
  priceFor: (route: ProviderRailRoute) => string;
  results: CreateManyResult[] | null;
}) {
  const { t } = useTranslations("paymentFees");
  const statusOf = (id: string) => results?.find((result) => result.key === id);

  return (
    <fieldset className="space-y-2 rounded-xl border border-border p-3">
      <legend className="px-1.5 text-xs font-medium">{t("edit_also_title")}</legend>
      <p className="text-xs text-muted-foreground">{t("edit_also_hint", { currency })}</p>
      <div className="flex flex-col gap-1.5">
        {options.map((option) => {
          const status = statusOf(option.id);
          return (
            <div key={option.id} className="flex items-center justify-between gap-3">
              <ColocaCheckbox
                checked={selected.includes(option.id)}
                onCheckedChange={(checked) =>
                  onChange(checked ? [...selected, option.id] : selected.filter((id) => id !== option.id))
                }
                label={<CorridorIdentity route={option} />}
                description={priceFor(option)}
              />
              {status && (
                <ColocaStatusPill variant={status.fee ? "success" : "danger"} size="sm">
                  {status.fee ? t("save_row_ok") : t("save_row_failed")}
                </ColocaStatusPill>
              )}
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}

/** `UTC−05:00` — next to the resolved instant, because two versions of a rule cannot share one. */
function utcOffsetLabel(): string {
  const minutes = -new Date().getTimezoneOffset();
  const sign = minutes >= 0 ? "+" : "−";
  const absolute = Math.abs(minutes);
  return `UTC${sign}${String(Math.floor(absolute / 60)).padStart(2, "0")}:${String(absolute % 60).padStart(2, "0")}`;
}
